import get from 'lodash.get';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { emptyArray } from 'ui-components/default-props/empty';
import { CustomsFormStatus } from '../../enums/customs-form-status';
import Global from '../../store/global-selectors';
import { messages } from './messages';

const BATCH_VALIDATED_ERROR_TYPE = 'error';
const BATCH_VALIDATED_WARNING_TYPE = 'warning';

const frontendMessages = (message) => {
  switch (message) {
    case 'No warehouse has been selected.':
      return <FormattedMessage {...messages.noShipFromOrderLabelError} />;
    default:
      return message;
  }
};

class BulkErrorsAndWarningsServiceClass {
  hasReceivedSubscriptionLimitError(response) {
    const errors = get(response, 'response.data.Errors') || emptyArray;
    return errors.map((err) => err.Message).includes('Subscription Error');
  }

  hasCreateLabelErrorWithFulfillmentPlanContext(errorResponse) {
    const errors = get(errorResponse, 'response.data.Errors') || emptyArray;
    return !!errors.filter((err) => err?.ErrorContext?.fulfillmentPlanId).length;
  }

  mapCreateLabelErrorWithFulfillmentPlanContextToShape(errors, getState) {
    return errors.map(({ Message, ErrorContext }) => {
      const [salesOrder] = Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(getState(), {
        fulfillmentPlanIds: [ErrorContext.fulfillmentPlanId],
      });
      return {
        error: { text: Message },
        fulfillmentPlanId: ErrorContext.fulfillmentPlanId,
        id: ErrorContext.fulfillmentPlanId,
        message: Message,
        messages: [Message],
        type: BATCH_VALIDATED_ERROR_TYPE,
        salesOrderId: salesOrder.salesOrderId,
        salesOrderNumber: salesOrder.orderNumber,
      };
    });
  }

  getBatchValidatedErrorType() {
    return BATCH_VALIDATED_ERROR_TYPE;
  }

  getBatchValidatedWarningType() {
    return BATCH_VALIDATED_WARNING_TYPE;
  }

  getPackingSlipPrintedWarning(mappedData) {
    return {
      id: 'printWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.packingSlipWarningMessage} />,
      isIgnorable: true,
      data: mappedData.map((datum) => ({
        ...datum,
        warning: {
          text: <FormattedMessage {...messages.packingSlipPrinted} />,
        },
      })),
    };
  }

  getBatchesPackingSlipPrintedWarning(mappedData) {
    return {
      id: 'printWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.batchPackingSlipPrintWarning} />,
      isIgnorable: true,
      isRemovable: false,
      data: mappedData.map((datum) => ({
        ...datum,
        warning: {
          text: <FormattedMessage {...messages.packingSlipPrinted} />,
        },
      })),
    };
  }

  getPrintWarningsTitle() {
    return messages.printWarnings;
  }

  getLabelPrintedWarning(mappedData) {
    return {
      id: 'printWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.labelWarningMessage} />,
      isIgnorable: true,
      data: mappedData.map((datum) => ({
        ...datum,
        warning: {
          text: <FormattedMessage {...messages.labelPrinted} />,
        },
      })),
    };
  }

  getBatchesLabelPrintedWarning(mappedData) {
    return {
      id: 'printWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.batchLabelPrintWarning} />,
      isIgnorable: true,
      isRemovable: false,
      data: mappedData.map((datum) => ({
        ...datum,
        warning: {
          text: <FormattedMessage {...messages.labelPrinted} />,
        },
      })),
    };
  }

  getLabelCreateErrorsTitle() {
    return messages.labelErrors;
  }

  getLabelCreateWarningsTitle() {
    return messages.labelWarnings;
  }

  getLabelBatchCreateErrors(data) {
    return {
      id: 'labelErrors',
      type: 'error',
      message: <FormattedMessage {...messages.batchErrors} />,
      isIgnorable: false,
      isRemovable: false,
      data: data.map((datum) => ({
        ...datum,
        error: {
          text: datum.message,
        },
      })),
    };
  }

  getLabelBatchCreateWarnings(data) {
    return {
      id: 'labelWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.batchWarnings} />,
      isIgnorable: true,
      data: data.map((datum) => ({
        ...datum,
        warning: {
          text: datum.message,
        },
      })),
    };
  }

  getLabelActiveWarnings(data) {
    return {
      id: 'activeLabelWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.alreadyActiveLabels} />,
      isIgnorable: true,
      isRemovable: false,
      data: data.map((datum) => ({
        ...datum,
        warning: {
          text: <FormattedMessage {...messages.activeLabel} />,
        },
      })),
    };
  }

  getLabelCreateErrors(data) {
    return {
      id: 'labelErrors',
      type: 'error',
      message: <FormattedMessage {...messages.labelCreateErrors} />,
      isIgnorable: false,
      data: data.map((datum) => ({
        ...datum,
        error: {
          text: frontendMessages(datum.message),
        },
      })),
    };
  }

  getLabelCreateWarnings(data) {
    return {
      id: 'labelWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.labelCreateWarnings} />,
      isIgnorable: true,
      data: data.map((datum) => ({
        ...datum,
        warning: {
          text: datum.message,
        },
      })),
    };
  }

  getCustomsFormsErrorTitle() {
    return messages.customsFormsErrors;
  }

  getPackingSlipWarnings(orders) {
    const mappedData = orders.reduce((sum, order) => {
      const { salesOrderId, orderNumber } = order.salesOrder;
      const alreadyPrintedFulfillmentPlans =
        order.fulfillmentPlans.filter((fulfillmentPlan) => get(fulfillmentPlan, 'packingSlipPrintedStatus.isPrinted')) || emptyArray;
      // TODO: if we add back the warnings for packing slips that are assigned to others use this
      // const assignedToSomeoneElseFulfillmentPlans = order.fulfillmentPlans.filter(plan => !!get(plan, 'assignedToUserId') && get(plan, 'assignedToUserId') !== currentUserId) || [];
      const printedFulfillmentPlans = alreadyPrintedFulfillmentPlans.map(({ fulfillmentPlanId }) => ({
        id: fulfillmentPlanId,
        salesOrderId,
        salesOrderNumber: orderNumber,
        fulfillmentPlanId,
        warning: {
          text: <FormattedMessage {...messages.packingSlipPrinted} />,
        },
      }));
      // TODO: if we add back the warnings for packing slips that are assigned to others use this
      // const assignedToOtherUserFulfillmentPlans = assignedToSomeoneElseFulfillmentPlans.map(({ fulfillmentPlanId }) => (
      //   {
      //     id: fulfillmentPlanId,
      //     salesOrderId,
      //     salesOrderNumber: orderNumber,
      //     fulfillmentPlanId,
      //   warning: {
      //     text: (<FormattedMessage {...messages.packingSlipAssignedToOther),
      //   },
      //   }
      // ));
      return [...sum, ...printedFulfillmentPlans];
    }, []);
    return {
      id: 'printWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.packingSlipWarningMessage} />,
      isIgnorable: true,
      data: [...mappedData],
    };
  }

  getSortedFulfillmentsByGridOrder(ordersGridIds, orders) {
    // To cover a case when a user(or seller?) does not have a preferred label sort order setting, we need to ensure the client requests the labels in the sort order they appear in the grid.
    // `ordersGridIds` is an array of ids from the grid, in the order they appear.
    // This dictionary with keys of ids and values of indices lets us sort/arrange fulfillments as they appear on the grid with linear bigO complexity.
    const fulfillmentPlanIdIndices = ordersGridIds.reduce((dict, fpId, index) => {
      if (!dict[fpId]) {
        return {
          ...dict,
          [fpId]: index,
        };
      }
      return dict;
    }, {});
    const fulfillments = orders
      .reduce(
        (sum, order) => [
          ...sum,
          ...get(order, 'fulfillments', emptyArray).filter((fulfillment) => !!fulfillment && !fulfillment.isLabelVoided),
        ],
        [],
      )
      .sort((aF, bF) => fulfillmentPlanIdIndices[aF.fulfillmentPlanId] - fulfillmentPlanIdIndices[bF.fulfillmentPlanId]);
    return fulfillments;
  }

  getLabelWarningDataFromListOfOrders(ordersGridIds, orders, currentUserId, hasShipmentsViewRestricted) {
    const fulfillments = this.getSortedFulfillmentsByGridOrder(ordersGridIds, orders);
    const fulfillmentPlans = [...new Set(orders.reduce((sum, order) => [...sum, ...get(order, 'fulfillmentPlans', emptyArray)], []))];
    const fulfillmentPlansBelongingToOthers = fulfillmentPlans.filter(
      (plan) => !!get(plan, 'assignedToUserId') && get(plan, 'assignedToUserId') !== currentUserId,
    );
    const fulfillmentIds = fulfillments.map((fulfillment) => fulfillment.fulfillmentId);
    const fulfillmentsWithPrintedLabels = fulfillments.filter((fulfillment) => get(fulfillment, 'labelPrintedStatus.isPrinted'));
    const someFulfillmentsHaveLabelsPrinted = fulfillmentsWithPrintedLabels.length > 0;
    const fulfillmentFulfillmentPlanIds = fulfillments.map((fulfillment) => fulfillment.fulfillmentPlanId);
    const fulfillmentPlanIds = [...new Set(fulfillmentFulfillmentPlanIds)];
    const fulfillmentPlanIdsWithPrintedWarnings = fulfillmentsWithPrintedLabels.map((fulfillment) => fulfillment.fulfillmentPlanId);

    return {
      orders,
      fulfillmentPlanIds,
      someFulfillmentsHaveLabelsPrinted,
      fulfillmentIds,
      fulfillmentPlanIdsWithPrintedWarnings,
      fulfillmentPlans,
      fulfillmentPlansBelongingToOthers,
      hasFulfillmentPlansBelongingToOthers: fulfillmentPlansBelongingToOthers.length > 0,
      hasShipmentsViewRestricted,
    };
  }

  getLabelWarnings(labelWarningData) {
    const { orders, fulfillmentPlanIdsWithPrintedWarnings, fulfillmentPlansBelongingToOthers, hasShipmentsViewRestricted } =
      labelWarningData;
    const mappedData = orders.reduce((sum, order) => {
      const { salesOrderId, orderNumber } = order.salesOrder;
      const orderFulfillmentPlansWithPrintedWarning = order.fulfillmentPlans.filter(
        (fulfillmentPlan) => fulfillmentPlanIdsWithPrintedWarnings.indexOf(fulfillmentPlan.fulfillmentPlanId) !== -1,
      );
      const printedPlansWarnings = orderFulfillmentPlansWithPrintedWarning.map(({ fulfillmentPlanId }) => ({
        id: fulfillmentPlanId,
        salesOrderId,
        salesOrderNumber: orderNumber,
        fulfillmentPlanId,
        warning: {
          text: <FormattedMessage {...messages.labelPrinted} />,
        },
      }));
      const bolongToOthersWarnings = !hasShipmentsViewRestricted
        ? emptyArray
        : fulfillmentPlansBelongingToOthers.map(({ fulfillmentPlanId }) => ({
            id: fulfillmentPlanId,
            salesOrderId,
            salesOrderNumber: orderNumber,
            fulfillmentPlanId,
            warning: {
              text: <FormattedMessage {...messages.belongsToAnotherWarning} />,
            },
          }));
      return [...sum, ...printedPlansWarnings, ...bolongToOthersWarnings];
    }, []);
    return {
      id: 'printWarnings',
      type: 'warning',
      message: <FormattedMessage {...messages.labelWarningMessage} />,
      isIgnorable: true,
      data: mappedData,
    };
  }

  getCustomsFormsErrorsFromCustomsStatusList(customsStatusList) {
    const statusToItemMap = {};
    customsStatusList.forEach((statusItem) => {
      const { status } = statusItem;
      if (!statusToItemMap[status]) {
        statusToItemMap[status] = [];
      }
      statusToItemMap[status].push(statusItem);
    });

    const errors = Object.keys(statusToItemMap)
      .map((status) => this.getErrorForCustomFormStatus(status, statusToItemMap[status]))
      .filter((error) => error !== undefined);

    return errors;
  }

  getErrorForCustomFormStatus(status, items) {
    if (!items) {
      return;
    }
    if (status === CustomsFormStatus.CustomsNotDeclared) {
      return {
        id: 'missingDeclarations',
        type: 'error',
        message: <FormattedMessage {...messages.missingCustomsDeclarationsTitle} />,
        isIgnorable: false,
        data: items.map((datum) => ({
          ...datum,
          id: datum.fulfillmentPlanId,
          error: {
            text: <FormattedMessage {...messages.missingCustomsDeclarations} />,
          },
        })),
      };
    }
    if (status === CustomsFormStatus.LabelNotCreated) {
      return {
        id: 'labelNotCreated',
        type: 'error',
        message: <FormattedMessage {...messages.customsMissingLabelTitle} />,
        isIgnorable: false,
        data: items.map((datum) => ({
          ...datum,
          id: datum.fulfillmentPlanId,
          error: {
            text: <FormattedMessage {...messages.customsMissingLabel} />,
          },
        })),
      };
    }
    if (status === CustomsFormStatus.CustomsNotRequired) {
      return {
        id: 'customsNotRequired',
        type: 'error',
        message: <FormattedMessage {...messages.customsNotRequiredTitle} />,
        isIgnorable: false,
        data: items.map((datum) => ({
          ...datum,
          id: datum.fulfillmentPlanId,
          error: {
            text: <FormattedMessage {...messages.customsNotRequired} />,
          },
        })),
      };
    }
    if (status === CustomsFormStatus.CustomsOnLabel) {
      return {
        id: 'customsOnLabel',
        type: 'error',
        message: <FormattedMessage {...messages.customsOnLabelTitle} />,
        isIgnorable: false,
        data: items.map((datum) => ({
          ...datum,
          id: datum.fulfillmentPlanId,
          error: {
            text: <FormattedMessage {...messages.customsOnLabel} />,
          },
        })),
      };
    }
  }

  canPrintCustomsForms(fulfillmentPlans, fulfillments, warehouses) {
    const canPrintAFulfillmentPlan = fulfillmentPlans.reduce((sum, fulfillmentPlan) => {
      const shipFrom = (warehouses || emptyArray).find((warehouse) => warehouse.shipFromId === fulfillmentPlan.shipFromId);
      if (!shipFrom) {
        return false;
      }

      if (shipFrom.countryCode === fulfillmentPlan.shipTo.countryCode && !fulfillmentPlan?.international?.needsCustoms) {
        return sum || false;
      }

      if (!fulfillmentPlan.customs || get(fulfillmentPlan, 'customs.customsItems.length', 0) === 0) {
        return sum || false;
      }

      const matchingFulfillment = fulfillments.find(
        (fulfillment) => fulfillment.fulfillmentPlanId === fulfillmentPlan.fulfillmentPlanId && !fulfillment.isLabelVoided,
      );
      if (!matchingFulfillment) {
        return sum || false;
      }

      if (!get(matchingFulfillment, 'labelFulfillment.hasCustomsFormFile', false)) {
        return sum || false;
      }

      return true;
    }, false);

    return canPrintAFulfillmentPlan;
  }

  getSingleCustomsFormError({ status }) {
    const errorData = {
      title: <FormattedMessage {...messages.singleCustomsErrorTitle} />,
    };
    if (status === CustomsFormStatus.CustomsNotDeclared) {
      errorData.message = <FormattedMessage {...messages.singleCustomsErrorMissingDeclarations} />;
    }
    if (status === CustomsFormStatus.LabelNotCreated) {
      errorData.message = <FormattedMessage {...messages.singleCustomsErrorNoLabel} />;
    }
    if (status === CustomsFormStatus.CustomsNotRequired) {
      errorData.message = <FormattedMessage {...messages.singleCustomsErrorDomesticShipment} />;
    }
    if (status === CustomsFormStatus.CustomsOnLabel) {
      errorData.message = <FormattedMessage {...messages.singleCustomsErrorFormOnLabel} />;
    }
    return errorData;
  }

  getCustomsFormsTitleByTypeCount(warningsAndErrors = emptyArray) {
    const hasError = warningsAndErrors.some((errOrWarn) => get(errOrWarn, 'type') === BATCH_VALIDATED_ERROR_TYPE);
    const hasWarning = warningsAndErrors.some((errOrWarn) => get(errOrWarn, 'type') === BATCH_VALIDATED_WARNING_TYPE);
    if (!hasError) {
      return messages.customsFormsWarnings;
    }
    if (!hasWarning) {
      return messages.customsFormsErrors;
    }
    return messages.customsFormsWarningsAndErrors;
  }

  getFulfillmentPlanAssignmentErrors(data) {
    return {
      id: 'batchedFulfillmentPlan',
      type: 'error',
      message: <FormattedMessage {...messages.fulfillmentPlanAssignmentErrorTitle} />,
      isIgnorable: false,
      data: data.map((datum) => ({
        ...datum,
        type: 'error',
        error: {
          text: <FormattedMessage {...messages.fulfillmentPlanAssignmentError} values={{ assignedUserName: datum.assignedUserName }} />,
        },
      })),
    };
  }

  getFulfillmentPlanAssignmentErrorTitle() {
    return messages.shipmentErrors;
  }

  erroredOrWarnedFulfillmentPlansFromJob(job) {
    return get(job, 'messages', emptyArray)
      .reduce((jobMessages, m) => [...jobMessages, ...get(m, 'messages', emptyArray)], [])
      .filter((m) => [this.getBatchValidatedErrorType(), this.getBatchValidatedWarningType()].includes(get(m, 'Type')));
  }

  uniqueErroredOrWarnedFulfillmentPlanIds(erroredOrWarnedFulfillmentPlans) {
    return erroredOrWarnedFulfillmentPlans.reduce((uniqueFpIds, currentFp) => {
      if (!uniqueFpIds.includes(currentFp.OrderID)) {
        uniqueFpIds.push(currentFp.OrderID);
      }
      return uniqueFpIds;
    }, []);
  }

  groupErrorsAndWarningsByFulfillmentPlanAndType(errorsAndWarnings) {
    const groupedErrorsAndWarnings = (errorsAndWarnings || []).reduce((acc, curr) => {
      const { type, id, message } = curr;
      const errWarnForFp = acc.find((entry) => entry.id === id);
      if (errWarnForFp && errWarnForFp.type === type) {
        errWarnForFp.messages.push(message);
        return acc;
      }
      acc.push({
        ...curr,
        messages: [message],
      });
      return acc;
    }, []);
    return groupedErrorsAndWarnings;
  }

  getBulkJobDetails({ excluded, erroredOrWarnedFulfillmentPlans, erroredFulfillmentPlanIds, andPrint, jobGuid, orders, fulfillmentPlans }) {
    const bulkJobDetails = {
      excluded,
      errored: erroredFulfillmentPlanIds.map((fpId) => fpId.toString()),
      andPrint,
      jobGuid,
    };

    const normalizedErrorsAndWarnings = erroredOrWarnedFulfillmentPlans.map(
      ({ OrderID: fulfillmentPlanId, Message: message, Type: type }) => ({
        fulfillmentPlanId: fulfillmentPlanId.toString(),
        message,
        type,
      }),
    );

    const erroredAndExcludedIds = [...erroredFulfillmentPlanIds, ...excluded.map((id) => parseInt(id, 10))];
    const ordersErrorsOrWarnings = orders.reduce((sum, order) => {
      const { salesOrderId, orderNumber } = order;
      const mappedFulfillmentPlans = order.fulfillmentPlanIds
        .filter((fpId) => erroredAndExcludedIds.includes(parseInt(fpId, 10)))
        .reduce((acc, fulfillmentPlanId) => {
          const errorsAndWarnings = normalizedErrorsAndWarnings.reduce((errAcc, currentErr) => {
            if (currentErr.fulfillmentPlanId === fulfillmentPlanId) {
              const errWarnObject = {
                id: fulfillmentPlanId,
                salesOrderId,
                salesOrderNumber: orderNumber,
                fulfillmentPlanId,
                message: currentErr.message,
                type: excluded.includes(fulfillmentPlanId) ? 'activeLabelWarning' : currentErr.type,
              };
              errAcc.push(errWarnObject);
            }
            return errAcc;
          }, []);
          return [...acc, ...errorsAndWarnings];
        }, []);
      return [...sum, ...mappedFulfillmentPlans];
    }, []);

    const itemType = 'salesOrder';
    const items = fulfillmentPlans.filter((fp) => excluded.indexOf(fp.fulfillmentPlanId) === -1);
    const mappedErrorsOrWarnings = ordersErrorsOrWarnings;
    const filteredErrors = mappedErrorsOrWarnings.filter((err) => get(err, 'type') === this.getBatchValidatedErrorType());
    const filteredWarnings = mappedErrorsOrWarnings.filter((err) => get(err, 'type') === this.getBatchValidatedWarningType());
    const filteredActiveLabelWarnings = mappedErrorsOrWarnings.filter((err) => get(err, 'type') === 'activeLabelWarning');
    const errors = filteredErrors.length ? [this.getLabelCreateErrors(filteredErrors)] : emptyArray;
    const warnings = filteredWarnings.length ? [this.getLabelCreateWarnings(filteredWarnings)] : emptyArray;
    const activeLabelWarnings = filteredActiveLabelWarnings.length
      ? [this.getLabelActiveWarnings(filteredActiveLabelWarnings)]
      : emptyArray;
    const modalTitle = filteredErrors.length > 0 ? this.getLabelCreateErrorsTitle() : this.getLabelCreateWarningsTitle();
    const errCollection = errors && errors.length && errors.length > 0 ? errors[0].data : [];
    const warningCollection = warnings && warnings.length && warnings.length > 0 ? warnings[0].data : [];
    const activeLabelWarningsCollection =
      activeLabelWarnings && activeLabelWarnings.length && activeLabelWarnings.length > 0
        ? activeLabelWarnings[0].data.map((warn) => ({
            ...warn,
            message: warn.warning.text,
          }))
        : [];

    return {
      bulkJobDetails,
      itemType,
      items,
      errors: errCollection,
      warnings: [...warningCollection, ...activeLabelWarningsCollection],
      activeLabelWarnings,
      filteredErrors,
      modalTitle,
    };
  }

  getIncludedExcludedErroredAndPrint = (job) => ({
    included: get(job, 'included', emptyArray),
    excluded: get(job, 'excluded', emptyArray),
    andPrint: get(job, 'andPrint', false),
    errored: get(job, 'errored', emptyArray),
  });

  mapErrorsAndWarningsToExpectedModel = (errorsAndWarnings) => {
    return errorsAndWarnings.map((errOrWarn) => ({
      ...errOrWarn,
      fulfillmentPlanId: errOrWarn.fulfillmentPlanId,
      orderNumber: errOrWarn.OrderNumber,
      salesOrderNumber: errOrWarn.OrderNumber,
      message: errOrWarn.type === 'error' ? errOrWarn.message : errOrWarn.warning.text,
      type: errOrWarn.type === 'error' ? 'error' : 'warning',
    }));
  };
}

export const BulkErrorsAndWarningsService = new BulkErrorsAndWarningsServiceClass();
